// The guided shot list for a result video, and the small capture helpers the recorder needs.
// One continuous clip; "Next shot" drops a chapter marker so a reviewer can jump straight to
// the scores. The order is a real sequence — it walks from where to what to who signed.

export interface IShot {
	key: 'unit' | 'sheet' | 'scores' | 'signatures';
	title: string;
	hint: string;
	/** Show the sheet-frame corner guides over the viewfinder. */
	frame: boolean;
}

export const SHOTS: IShot[] = [
	{ key: 'unit', title: 'Show the polling unit', hint: 'The signboard or the place the count happened.', frame: false },
	{ key: 'sheet', title: 'Show the whole sheet', hint: 'Fill the frame and pan slowly from top to bottom.', frame: true },
	{ key: 'scores', title: 'Hold on the party scores', hint: 'Keep each row steady for a second so it can be read.', frame: true },
	{
		key: 'signatures',
		title: 'Show the signatures and stamp',
		hint: 'The presiding officer’s signature and the stamp.',
		frame: true,
	},
];

export const shotLabel = (key: string): string => SHOTS.find((s) => s.key === key)?.title ?? key;

// Shared with cancellation evidence; re-exported so existing imports keep working.
export { MAX_HASH_BYTES, sha256Hex } from './media-relay';

/** Prefer MP4 (plays everywhere incl. iOS); most Android browsers only record WebM. */
export const pickRecorderMime = (): string => {
	if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
	const candidates = [
		'video/mp4;codecs=avc1',
		'video/mp4',
		'video/webm;codecs=vp9,opus',
		'video/webm;codecs=vp8,opus',
		'video/webm',
	];
	return candidates.find((t) => MediaRecorder.isTypeSupported(t)) ?? '';
};

/** In-app recording is what earns the "Verified capture" label; without it the agent picks a file. */
export const canRecordInApp = (): boolean =>
	typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';

export const extensionFor = (mime: string): string => {
	const base = mime.split(';')[0].trim();
	if (base === 'video/mp4') return 'mp4';
	if (base === 'video/quicktime') return 'mov';
	if (base === 'video/3gpp') return '3gp';
	return 'webm';
};

export const deviceInfo = (cameraLabel?: string): string => {
	const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
	return [cameraLabel, ua].filter(Boolean).join(' | ').slice(0, 500);
};

export const formatClock = (seconds: number): string => {
	const s = Math.max(0, Math.floor(seconds));
	return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export const formatMegabytes = (bytes: number): string =>
	`${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;

/** "18:40 today" / "09:15 tomorrow" — the window deadline in the agent's own words. */
export const formatDeadline = (iso: string | null | undefined, now = new Date()): string | null => {
	if (!iso) return null;
	const at = new Date(iso);
	if (Number.isNaN(at.getTime())) return null;
	const time = at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
	const days = Math.round((startOfDay(at) - startOfDay(now)) / 86_400_000);
	if (days === 0) return `${time} today`;
	if (days === 1) return `${time} tomorrow`;
	return `${time}, ${at.toLocaleDateString([], { day: 'numeric', month: 'short' })}`;
};
